/**
 * The team surface's wire — invites and memberships, plan §2 U64.
 *
 * Below {@link ROLE_CHOICE_TIER} every invite and every role change names `editor`, and any
 * other role answers `PLAN_GATED`. Seats are free at every tier.
 */
import { z } from "zod";
import { ROLES } from "../roles.js";
/** Choosing a role other than `editor` is Growth's. */
export const ROLE_CHOICE_TIER = "growth";
/** The role every invite carries below {@link ROLE_CHOICE_TIER}. */
export const DEFAULT_INVITE_ROLE = "editor";
/** How long an invite link works. Plan §8 default 18. */
export const INVITE_TTL_DAYS = 7;
/** 32 random bytes, base64url, unpadded. */
export const INVITE_TOKEN = /^[A-Za-z0-9_-]{43}$/;
const EMAIL_MESSAGE = "That is not an email address we can send an invitation to.";
const ROLE_MESSAGE = `role must be one of: ${ROLES.join(", ")}.`;
const TOKEN_MESSAGE = "That invitation link is not complete. Open it again from the email, or ask for a new one.";
export const createInviteSchema = z
    .object({
    email: z
        .string(EMAIL_MESSAGE)
        .transform((raw) => raw.trim().toLowerCase())
        .pipe(z.email(EMAIL_MESSAGE)),
    role: z.enum(ROLES, ROLE_MESSAGE).default(DEFAULT_INVITE_ROLE),
})
    .strict()
    .describe("POST /v1/invites — invite someone to this organisation by email.");
export const inviteIdSchema = z
    .object({ id: z.uuid() })
    .describe("DELETE /v1/invites/{id} and POST /v1/invites/{id}/resend.");
export const acceptInviteSchema = z
    .object({ token: z.string(TOKEN_MESSAGE).regex(INVITE_TOKEN, TOKEN_MESSAGE) })
    .strict()
    .describe("POST /v1/invites/accept — join the organisation an invitation names.");
export const setMemberRoleSchema = z
    .object({ userId: z.uuid(), role: z.enum(ROLES, ROLE_MESSAGE) })
    .strict()
    .describe("PATCH /v1/orgs/{id}/members/{userId} — change one member's role.");
export const removeMemberSchema = z
    .object({ userId: z.uuid() })
    .strict()
    .describe("DELETE /v1/orgs/{id}/members/{userId} — take someone out of this organisation.");
export const inviteSummarySchema = z.object({
    id: z.uuid(),
    email: z.string(),
    role: z.enum(ROLES),
    sentAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
});
export const inviteListSchema = z.object({ invites: z.array(inviteSummarySchema) });
export const inviteRevokedSchema = z.object({ id: z.uuid() });
export const inviteAcceptedSchema = z.object({
    orgId: z.uuid(),
    orgName: z.string(),
    role: z.enum(ROLES),
});
export const memberSummarySchema = z.object({
    userId: z.uuid(),
    name: z.string().nullable(),
    email: z.string(),
    role: z.enum(ROLES),
    /**
     * The newest `session.updated_at`. Better Auth moves it at most once per
     * `SESSION_UPDATE_AGE_SECONDS` (a day), so activity lies within the day after it. Null
     * when no session row exists: signed out everywhere.
     */
    lastActiveAt: z.iso.datetime().nullable(),
});
export const memberListSchema = z.object({ members: z.array(memberSummarySchema) });
export const memberRoleSetSchema = z.object({ userId: z.uuid(), role: z.enum(ROLES) });
export const memberRemovedSchema = z.object({ userId: z.uuid() });
export const transferOwnershipSchema = z
    .object({ userId: z.uuid() })
    .strict()
    .describe("POST /v1/orgs/{id}/transfer-owner — make another member the owner and step down.");
export const ownershipTransferredSchema = z.object({
    ownerUserId: z.uuid(),
    formerOwnerUserId: z.uuid(),
    formerOwnerRole: z.enum(ROLES),
});
//# sourceMappingURL=team.js.map